"use client"

import ChevronSVG from "images/Icons/Chevron.inline.svg"
import KickerCreditSVG from "images/Icons/KickerCredit.inline.svg"
import { eases } from "library/eases"
import UniversalLink from "library/link"
import { css, f, fmobile, fresponsive, ftablet, styled } from "library/styled"
import { useMedia } from "library/useMedia"
import { useEffect, useRef, useState } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

const accordionData = [
	{
		id: "01",
		title: "Price Transparency",
		content:
			"Know the cost of every visit before you book—no hidden fees, no surprise bills.",
	},
	{
		id: "02",
		title: "Preventive First",
		content:
			"$0 primary-care copays and covered screenings push members to act early—before small issues get pricey.",
	},
	{
		id: "03",
		title: "Flexible Plans",
		content:
			"Multiple plan options so every budget finds the right fit without sacrificing quality.",
	},
	{
		id: "04",
		title: "Price Stability",
		content:
			"Data-driven underwriting locks in predictable renewals, keeping year-over-year increases in check.",
	},
]

export type AccordionItemType = {
	id: string
	title: string
	content: string
}

type AccordionProps = {
	items?: AccordionItemType[]
}

const CARD_ANIMATION_DURATION = "0.5s"
const TRANSITION_EASING = eases.cubic.inOut
const BORDER_STYLE = `1px solid ${colors.green100}`
const Z_INDICES = { content: 1, header: 2 }
const STANDARD_TRANSITION = `${CARD_ANIMATION_DURATION} ${TRANSITION_EASING}`

export default function AccordionComponent({
	items = accordionData,
}: AccordionProps) {
	const [activeIndex, setActiveIndex] = useState<number | null>(0)
	const accordionRef = useRef<HTMLDivElement>(null)
	const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
	const isMobile = useMedia(false, false, false, true)
	const isTablet = useMedia(false, false, true, false)
	const isDesktop = !isMobile && !isTablet

	useEffect(() => {
		if (!isMobile) return

		const handleClickOutside = (event: MouseEvent) => {
			if (
				accordionRef.current &&
				!accordionRef.current.contains(event.target as Node)
			) {
				setActiveIndex(null)
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () => {
			document.removeEventListener("mousedown", handleClickOutside)
		}
	}, [isMobile])

	const handleInteraction = (index: number) => {
		if (!isDesktop) return
		setActiveIndex(activeIndex === index ? null : index)
	}

	const handleMouseEnter = (index: number) => {
		if (!isDesktop) return
		setActiveIndex(index)
	}

	const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
		if (!isDesktop) return

		switch (e.key) {
			case " ":
			case "Enter":
				e.preventDefault()
				setActiveIndex(activeIndex === index ? null : index)
				break
			case "ArrowDown":
			case "ArrowRight": {
				e.preventDefault()
				const nextIndex = (index + 1) % items.length
				itemRefs.current[nextIndex]?.focus()
				break
			}
			case "ArrowUp":
			case "ArrowLeft": {
				e.preventDefault()
				const prevIndex = (index - 1 + items.length) % items.length
				itemRefs.current[prevIndex]?.focus()
				break
			}
			case "Home":
				e.preventDefault()
				itemRefs.current[0]?.focus()
				break
			case "End":
				e.preventDefault()
				itemRefs.current[items.length - 1]?.focus()
				break
			default:
				break
		}
	}

	useEffect(() => {
		itemRefs.current = itemRefs.current.slice(0, items.length)
		while (itemRefs.current.length < items.length) {
			itemRefs.current.push(null)
		}
	}, [items])

	const setRef = (el: HTMLButtonElement | null, index: number) => {
		itemRefs.current[index] = el
	}

	return (
		<AccordionContainer ref={accordionRef} aria-label="Arlo values">
			{items.map((item, index) => (
				<AccordionCard
					key={item.id}
					position={index}
					isActive={activeIndex === index}
					activeIndex={activeIndex ?? -1}
					onClick={() => handleInteraction(index)}
					onMouseEnter={() => handleMouseEnter(index)}
					onKeyDown={(e) => handleKeyDown(e, index)}
				>
					<Header
						type="button"
						ref={(el: HTMLButtonElement | null) => setRef(el, index)}
						id={`header-${item.id}`}
						isOpen={activeIndex === index}
						aria-expanded={activeIndex === index}
						aria-controls={`panel-${item.id}`}
					>
						<Kicker>
							<KickerCredit /> <ItemId>{item.id}</ItemId>
						</Kicker>
						<Row>
							<ItemTitle>{item.title}</ItemTitle>
							<ToggleIcon isOpen={activeIndex === index} aria-hidden="true" />
						</Row>
					</Header>
					<CardContent
						isOpen={activeIndex === index}
						id={`panel-${item.id}`}
						aria-labelledby={`header-${item.id}`}
					>
						<ContentText>{item.content}</ContentText>
					</CardContent>
				</AccordionCard>
			))}
		</AccordionContainer>
	)
}

const AccordionContainer = styled("section", () => {
	return {
		...fresponsive(css`
			width: 100%;
			display: grid;
			grid-template:
				"card1 card2 card3 card4" 213px
				/ 1fr 1fr 1fr 1fr;
			height: 100%;
		`),
		...ftablet(css`
			grid-template:
				"card1 card2" 220px
				"card3 card4" 220px
				/ 480px 480px;
			position: relative;
			border: ${BORDER_STYLE};
			border-radius: 24px 24px 0 0;
			overflow: clip;
		`),
		...fmobile(css`
			display: flex;
			grid-template: none;
			flex-direction: column;
			height: auto;
			gap: 0;
			border: ${BORDER_STYLE};
			border-radius: 16px 16px 0 0;
		`),
	}
})

const AccordionCard = styled(
	"div",
	({
		isActive,
		position,
		activeIndex,
	}: {
		isActive?: boolean
		position: number
		activeIndex: number | null
	}) => {
		const gridArea = `card${position + 1}`

		return {
			...f.large(css`
				position: relative;
				background-color: ${colors.offWhite};
				border-top: ${BORDER_STYLE};
				border-bottom: ${BORDER_STYLE};
				border-left: 1px solid
					${position === 0 ? colors.green100 : "transparent"};
				border-right: ${BORDER_STYLE};
				border-radius: ${position === 0
					? "8px 0 0 0"
					: position === 3
						? "0 8px 0 0"
						: "0"};
				transition:
					border-radius ${STANDARD_TRANSITION},
					border-color ${STANDARD_TRANSITION},
					height ${STANDARD_TRANSITION};
				overflow: clip;
				height: ${isActive ? "213px" : "100px"};
				padding: 0 28px;
				z-index: ${Z_INDICES.content};
				transform: translateZ(0);
				will-change: height, border-radius, z-index;

				${isActive
					? css`
							border-left: ${BORDER_STYLE};
							border-radius: ${position === 0
								? "8px 0 0 8px"
								: position === 3
									? "0 8px 8px 0"
									: "0"};
						`
					: ""}

				${position === 0 && activeIndex === 1
					? css`
							border-right-color: transparent;
						`
					: ""}
          
          ${position === 1 && activeIndex === 2
					? css`
							border-right-color: transparent;
						`
					: ""}
          
          ${position === 2 && activeIndex === 3
					? css`
							border-right-color: transparent;
						`
					: ""}
			`),

			...f.tablet(css`
				grid-area: ${gridArea};
				height: 220px;
				cursor: default;
				border-color: transparent;
				border-radius: 0;
				pointer-events: none;
				padding: 0 32px;

				&:nth-of-type(1) {
					border-right: ${BORDER_STYLE};
					border-bottom: ${BORDER_STYLE};
				}

				&:nth-of-type(2) {
					border-left: none;
					border-bottom: ${BORDER_STYLE};
				}

				&:nth-of-type(3) {
					border-right: ${BORDER_STYLE};
				}
			`),

			...f.mobile(css`
				height: auto;
				padding: 0 20px;

				&:nth-of-type(1) {
					border-top: none;
					border-right: none;
					border-left: none;
				}

				&:nth-of-type(2),
				&:nth-of-type(3),
				&:nth-of-type(4) {
					border-right: none;
					border-left: none;
					border-top: ${BORDER_STYLE};
				}
			`),
		}
	},
)

const Header = styled(UniversalLink, ({ isOpen }: { isOpen?: boolean }) => {
	return {
		...fresponsive(css`
			display: flex;
			flex-direction: column;
			align-items: center;
			height: ${isOpen ? "90px" : "100px"};
			position: relative;
			gap: 12px;
			z-index: ${Z_INDICES.header};
			padding: 32px 0 0;
			width: 100%;
			background: none;
			border: none;
			text-align: left;
			cursor: pointer;
			transform: translateZ(0);
			backface-visibility: hidden;
			will-change: transform, height;
			transition: height ${STANDARD_TRANSITION};

			&:focus-visible {
				outline: 2px solid ${colors.green400};
				outline-offset: -2px;
			}
		`),

		...ftablet(css`
			height: 106px;
			padding: 48px 0 0;
			cursor: default;
		`),

		...fmobile(css`
			height: 75px;
			min-height: 75px;
			padding: 16px 0;
			justify-content: center;
		`),
	}
})

const CardContent = styled("div", ({ isOpen }: { isOpen?: boolean }) => {
	return {
		...fresponsive(css`
			padding: ${isOpen ? "0" : "16px 0"};
			height: 158px;
			opacity: 1;
			pointer-events: ${isOpen ? "auto" : "none"};
			transition:
				padding ${STANDARD_TRANSITION},
				opacity ${STANDARD_TRANSITION};
			color: ${colors.green300};
			position: relative;
			z-index: ${Z_INDICES.content};
			transform: translateZ(0);
			backface-visibility: hidden;
			will-change: transform, opacity;
		`),

		...ftablet(css`
			padding: 16px 0;
			height: 100px;
			transition: none;
		`),

		...fmobile(css`
			padding: 0 0 16px;
			height: auto;
			transition: none;
			overflow: clip;
		`),
	}
})

const ItemId = styled("span", () => {
	return {
		...fresponsive(css`
			${textStyles.titleS};
			color: ${colors.green300};
			margin-right: 12px;
		`),
		...ftablet(css`
			${textStyles.titleR};
		`),
	}
})

const ItemTitle = styled("h1", () => {
	return {
		...fresponsive(css`
			${textStyles.bodyR};
			color: ${colors.black100};
			margin: 0;
			flex-grow: 1;
		`),

		...ftablet(css`
			${textStyles.bodyL};
		`),

		...fmobile(css`
			${textStyles.bodyR};
		`),
	}
})

const ToggleIcon = styled(ChevronSVG, ({ isOpen }: { isOpen?: boolean }) => {
	return {
		...fresponsive(css`
			transform: ${isOpen ? "rotate(180deg)" : "rotate(0deg)"};
			transition: transform 0.3s ${TRANSITION_EASING};
			width: 12px;
			height: 8px;
			will-change: transform;
			backface-visibility: hidden;
		`),

		...ftablet(css`
			display: none;
		`),

		...fmobile(css`
			display: none;
		`),
	}
})

const ContentText = styled("p", () => {
	return {
		...fresponsive(css`
			${textStyles.bodySL};
			color: ${colors.green300};
			margin: 0;
		`),
	}
})

const Row = styled("div", () => {
	return {
		...fresponsive(css`
			display: flex;
			align-items: center;
			justify-content: space-between;
			width: 100%;
		`),
	}
})

const Kicker = styled("div", () => {
	return {
		...fresponsive(css`
			display: flex;
			align-self: flex-start;
			gap: 4px;
		`),
	}
})

const KickerCredit = styled(KickerCreditSVG, () => {
	return {
		...fresponsive(css`
			color: ${colors.green400};
			width: 16px;
			height: 9px;
		`),
	}
})
